import { ChangeEvent, useState } from 'react';
import { t } from 'i18next';
import { toast } from 'react-toastify';
import { SelectChangeEvent } from '@mui/material';

import { CategoryType } from '@views/pages/Patient/components/Documents/hooks/useDocumentsTable.ts';
import { usePatientDocumentUploadMutation } from '@common/cache';
import { formatApiError } from '@common/errors/error-formatting.ts';

const UPLOAD_TOAST_ID = 'patient-file-upload';

export const usePatientFileUploadModal = ({
  patient_id,
  task_id,
  handleClose,
}: {
  patient_id: string;
  handleClose: () => void;
  task_id?: string;
}) => {
  const [selectedCategory, setSelectedCategory] =
    useState<CategoryType>('identification');
  const [specification, setSpecification] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [isSaveChangesModalOpen, setIsSaveChangesModalOpen] = useState(false);

  const {
    mutateAsync: uploadDocument,
    reset,
    isPending,
  } = usePatientDocumentUploadMutation(patient_id);

  const categoryOptions = [
    { id: 'care_plan', label: t('common.categoryOptions.care_plan') },
    {
      id: 'caregiver_document',
      label: t('common.categoryOptions.caregiver_document'),
    },
    { id: 'community_fax', label: t('common.categoryOptions.community_fax') },
    { id: 'general_order', label: t('common.categoryOptions.general_order') },
    {
      id: 'home_health_order',
      label: t('common.categoryOptions.home_health_order'),
    },
    { id: 'identification', label: t('common.categoryOptions.identification') },
    { id: 'insurance', label: t('common.categoryOptions.insurance') },
    { id: 'legal_document', label: t('common.categoryOptions.legal_document') },
    { id: 'med_list', label: t('common.categoryOptions.med_list') },
    { id: 'medical_record', label: t('common.categoryOptions.medical_record') },
    {
      id: 'medical_record_request',
      label: t('common.categoryOptions.medical_record_request'),
    },
    { id: 'referral', label: t('common.categoryOptions.referral') },
    {
      id: 'respite_invoice',
      label: t('common.categoryOptions.respite_invoice'),
    },
    { id: 'system_letter', label: t('common.categoryOptions.system_letter') },
    { id: 'result', label: t('common.categoryOptions.result') },
    { id: 'other', label: t('common.categoryOptions.other') },
  ];

  const handleChangeCategory = (event: SelectChangeEvent<string>) => {
    setSelectedCategory(event.target.value as CategoryType);
  };

  const handleChangeSpecification = (event: ChangeEvent<HTMLInputElement>) => {
    setSpecification(event.target.value);
  };

  const handleChangeFile = (newFile: File | null) => {
    setFile(newFile);
    reset();
  };

  const handleCloseAction = () => {
    if (file) {
      setIsSaveChangesModalOpen(true);
    } else {
      handleClose();
      setFile(null);
      setSelectedCategory('identification');
      setSpecification('');
    }
  };

  const handleUploadDocument = async () => {
    if (!file) return;

    toast.loading('Uploading file...', {
      toastId: UPLOAD_TOAST_ID,
    });

    const formData = new FormData();
    formData.append('file', file);
    formData.append('category', selectedCategory);
    if (selectedCategory === 'other') {
      formData.append('other_specification', specification);
    }
    if (task_id) {
      formData.append('task_id', task_id);
    }

    try {
      await uploadDocument({
        file: formData,
      });
      toast.update(UPLOAD_TOAST_ID, {
        render: 'You uploaded a new document!',
        type: 'success',
        isLoading: false,
        autoClose: 2000,
      });
      setFile(null);

      setSelectedCategory('identification');
      setSpecification('');
      setIsSaveChangesModalOpen(false);
      handleClose();
    } catch (error) {
      toast.update(UPLOAD_TOAST_ID, {
        render: formatApiError(error),
        type: 'error',
        isLoading: false,
        autoClose: 2000,
      });
    }
  };

  const handleCloseSaveChangesModal = () => {
    setIsSaveChangesModalOpen(false);
  };

  const handleDiscardChanges = () => {
    setIsSaveChangesModalOpen(false);
    handleClose();
    setFile(null);
    setSelectedCategory('identification');
    setSpecification('');
  };

  return {
    categoryOptions: categoryOptions.map(o => ({
      value: o.id,
      label: o.label,
    })),
    handleChangeCategory,
    selectedCategory,
    handleChangeFile,
    handleUploadDocument,
    isUploadDisabled:
      !file || isPending || (selectedCategory === 'other' && !specification),
    file,
    setFile,
    handleCloseAction,
    isSaveChangesModalOpen,
    handleCloseSaveChangesModal,
    handleDiscardChanges,
    isValid: true,
    isFileUploading: isPending,
    handleChangeSpecification,
    specification,
  };
};
